import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { FaUserCircle } from 'react-icons/fa';
import { MdOutlineAssessment } from 'react-icons/md';
import { removeDocument } from 'redux/booking/car/carBookingSlice';
import { Card, Badge, Alert, Button } from 'components/common';
import TransactionDetailAttachment from './TransactionDetailAttachments';
import ApprovalDriverList from './ApprovalDriverList';
import TransactionDetailDriverTrans from './TransactionDetailDriverTrans';
import TransactionDetailUploadAttachmentDirectly from './TransactionDetailUploadAttachmentDirectly';
import TransactionDetailBookingInfo from './TransactionDetailBookingInfo';
import AssignDriverFormSingle from './AssignDriverFormSingle';

// status_id → Alert type
const ALERT_TYPE = { 1: 'warning', 2: 'success', 3: 'error', 4: 'error', 5: 'info' };

const Field = ({ label, children, danger = false }) => (
  <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-0.5 sm:tw-gap-2 tw-mb-1.5 tw-text-sm">
    <span className="sm:tw-w-32 sm:tw-flex-shrink-0 tw-font-semibold tw-text-slate-600">{label}</span>
    <span className={`${danger ? 'tw-text-red-600' : 'tw-text-blue-700'} tw-min-w-0 tw-break-words`}>{children}</span>
  </div>
);
Field.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node, danger: PropTypes.bool };

const TransactionDetailInfo = ({ transaction = {} }) => {
  const [showAssignDriver, setShowAssignDriver] = useState(false);
  const dispatch = useDispatch();
  const statusId = transaction?.status_id;
  const documents = transaction?.documents || [];

  const { allow_upload_document, allow_remove_document, allow_assign_driver } = transaction.permissions || {};

  const isWaitingApproval = statusId === 1 || statusId === 5;
  const isRejected = statusId === 3;
  const isApproved = statusId === 2;

  const handleRemoveAttachment = (documentId) => {
    dispatch(removeDocument({ transactionId: transaction?.id, documentId }));
  };

  return (
    <>
      <Alert type={ALERT_TYPE[statusId] || 'info'} className="tw-mb-3">
        {transaction?.action_message}
      </Alert>

      <TransactionDetailBookingInfo transaction={transaction} />

      {!isWaitingApproval && (
        <>
          <Card
            padding="none"
            className="tw-mb-3"
            header={(
              <div className="tw-flex tw-items-center tw-justify-between">
                <h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800">Approval information</h5>
                {allow_assign_driver && (
                  <Button size="sm" variant="secondary" leftIcon={MdOutlineAssessment} onClick={() => setShowAssignDriver(true)}>
                    Assign Driver
                  </Button>
                )}
              </div>
            )}
          >
            <div className="tw-bg-slate-50 tw-p-4">
              {isRejected ? (
                <div>
                  <h6 className="tw-text-sm tw-font-semibold tw-text-red-600 tw-mb-3">Booking rejected with some reason below:</h6>
                  <Field label="Reason" danger>{transaction.remark}</Field>
                  <Field label="CC emails">
                    <span className="tw-flex tw-flex-wrap tw-gap-1">
                      {(transaction?.cc_email || []).map((item, i) => <Badge key={i} variant="info" size="sm">{item}</Badge>)}
                    </span>
                  </Field>
                  <Field label="Rejected by">{transaction?.approved_by}</Field>
                  <Field label="Rejected date">{transaction?.approved_date}</Field>
                </div>
              ) : (
                <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6">
                  <div>
                    <div className="tw-mb-2 tw-text-sm tw-text-slate-700">Booking assigned to drivers below:</div>
                    <ApprovalDriverList transaction={transaction} />
                  </div>
                  <div>
                    <Field label="CC emails">
                      <span className="tw-flex tw-flex-wrap tw-gap-1">
                        {(transaction?.cc_email || []).map((item, i) => <Badge key={i} variant="info" size="sm">{item}</Badge>)}
                      </span>
                    </Field>
                    <Field label="Notes" danger><span dangerouslySetInnerHTML={{ __html: transaction?.remark }} /></Field>
                    <Field label="Approved by"><FaUserCircle className="tw-inline tw-mr-1" /> {transaction?.approved_by}</Field>
                    <Field label="Approved date">{transaction?.approved_date}</Field>

                    <h6 className="tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-text-slate-500 tw-mt-4 tw-mb-3">Attachments</h6>

                    {documents.length > 0 && (
                      <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mb-3">
                        {documents.map((attachment) => (
                          <TransactionDetailAttachment
                            {...attachment}
                            key={attachment.id}
                            allow_remove={allow_remove_document}
                            handleRemoveAttachment={handleRemoveAttachment}
                          />
                        ))}
                      </div>
                    )}

                    {allow_upload_document && (
                      <TransactionDetailUploadAttachmentDirectly transactionId={transaction?.id} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {isApproved && (
            <Card
              padding="none"
              className="tw-mb-3"
              header={<h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800">Car and Driver transactions</h5>}
            >
              <div className="tw-bg-slate-50 tw-p-4">
                <TransactionDetailDriverTrans transaction={transaction} />
              </div>
            </Card>
          )}
        </>
      )}

      {showAssignDriver && (
        <AssignDriverFormSingle
          showModal={showAssignDriver}
          setshowModal={setShowAssignDriver}
          transaction={transaction}
        />
      )}
    </>
  );
};

TransactionDetailInfo.propTypes = {
  transaction: PropTypes.object.isRequired,
};

export default TransactionDetailInfo;
